/**
 * React Query hooks for auction data
 */

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { auctionService } from '@/services/auctions';
import type { Auction, AuctionPeriod, AuctionParams } from '@/types/auctions';

// Query keys for auction data
const auctionKeys = {
  all: ['auctions'] as const,
  list: () => [...auctionKeys.all, 'list'] as const,
  period: () => [...auctionKeys.all, 'period'] as const,
  params: () => [...auctionKeys.all, 'params'] as const,
};

/**
 * Hook to fetch all active auctions
 */
export function useAuctions(): UseQueryResult<Auction[], Error> {
  return useQuery<Auction[], Error>({
    queryKey: auctionKeys.list(),
    queryFn: () => auctionService.getAuctions(),
    staleTime: 10_000, // 10 seconds (matches backend cache TTL)
    refetchInterval: 10_000, // Auto-refetch every 10 seconds
  });
}

/**
 * Hook to fetch auction period with timer information
 */
export function useAuctionPeriod(): UseQueryResult<AuctionPeriod, Error> {
  return useQuery<AuctionPeriod, Error>({
    queryKey: auctionKeys.period(),
    queryFn: () => auctionService.getAuctionPeriod(),
    staleTime: 10_000, // 10 seconds
    refetchInterval: 10_000, // Auto-refetch to keep timer fresh
  });
}

/**
 * Hook to fetch auction parameters (min bid fee, etc.)
 */
export function useAuctionParams(): UseQueryResult<AuctionParams, Error> {
  return useQuery<AuctionParams, Error>({
    queryKey: auctionKeys.params(),
    queryFn: () => auctionService.getAuctionParams(),
    staleTime: 300_000, // 5 minutes (params rarely change)
  });
}

/**
 * Hook to invalidate auction queries
 */
export function useInvalidateAuctions(): {
  invalidateAuctions: () => Promise<void>;
  invalidatePeriod: () => Promise<void>;
  invalidateParams: () => Promise<void>;
  invalidateAll: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  return {
    invalidateAuctions: (): Promise<void> => queryClient.invalidateQueries({ queryKey: auctionKeys.list() }),
    invalidatePeriod: (): Promise<void> => queryClient.invalidateQueries({ queryKey: auctionKeys.period() }),
    invalidateParams: (): Promise<void> => queryClient.invalidateQueries({ queryKey: auctionKeys.params() }),
    invalidateAll: (): Promise<void> => queryClient.invalidateQueries({ queryKey: auctionKeys.all }),
  };
}
